import { useRef, useEffect, useCallback } from 'react';

interface CompletionCelebrationProps {
  show: boolean;
  onComplete?: () => void;
}

// Brand-inspired colors
const COLORS = {
  red: '#e61515',
  blue: '#2563eb',
  gold: '#f59e0b',
  emerald: '#22c55e',
  purple: '#8b5cf6',
  pink: '#ec4899',
  cyan: '#06b6d4',
  white: '#ffffff',
};

const PALETTE = [COLORS.red, COLORS.blue, COLORS.gold, COLORS.emerald, COLORS.purple, COLORS.pink, COLORS.cyan];

function randomColor() {
  return PALETTE[Math.floor(Math.random() * PALETTE.length)];
}

function lerp(a: number, b: number, t: number) {
  return a + (b - a) * t;
}

// ─── Animation 1: Multiple Firework Bursts ──────────────────────────────────
function createFireworks(w: number, h: number) {
  const margin = w * 0.15;

  const burstConfigs = [
    { x: margin + Math.random() * w * 0.2, y: h * 0.2 + Math.random() * h * 0.2, time: 0.0, count: 30 },
    { x: w - margin - Math.random() * w * 0.2, y: h * 0.15 + Math.random() * h * 0.2, time: 0.1, count: 28 },
    { x: w * 0.35 + Math.random() * w * 0.3, y: h * 0.25 + Math.random() * h * 0.15, time: 0.2, count: 32 },
    { x: w * 0.15 + Math.random() * w * 0.15, y: h * 0.4 + Math.random() * h * 0.15, time: 0.28, count: 22 },
    { x: w * 0.65 + Math.random() * w * 0.15, y: h * 0.2 + Math.random() * h * 0.2, time: 0.36, count: 25 },
  ];

  interface FWParticle {
    x: number; y: number;
    vx: number; vy: number;
    color: string;
    size: number;
    trail: { x: number; y: number }[];
  }

  const bursts: { particles: FWParticle[]; startTime: number; cx: number; cy: number }[] = [];

  burstConfigs.forEach(cfg => {
    const c1 = randomColor();
    const c2 = randomColor();
    const particles: FWParticle[] = Array.from({ length: cfg.count }, () => {
      const angle = Math.random() * Math.PI * 2;
      const speed = 15 + Math.random() * 55;
      return {
        x: cfg.x, y: cfg.y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        color: Math.random() > 0.4 ? c1 : c2,
        size: 1 + Math.random() * 1.5,
        trail: [],
      };
    });
    bursts.push({ particles, startTime: cfg.time, cx: cfg.x, cy: cfg.y });
  });

  return (ctx: CanvasRenderingContext2D, t: number, dt: number) => {
    const fadeOut = t > 0.75 ? 1 - (t - 0.75) / 0.25 : 1;

    bursts.forEach(burst => {
      if (t < burst.startTime) return;
      const elapsed = t - burst.startTime;

      // White flash at burst moment
      if (elapsed < 0.05) {
        ctx.beginPath();
        ctx.arc(burst.cx, burst.cy, 10, 0, Math.PI * 2);
        ctx.fillStyle = COLORS.white;
        ctx.globalAlpha = (1 - elapsed / 0.05) * 0.5;
        ctx.fill();
      }

      burst.particles.forEach(p => {
        p.trail.push({ x: p.x, y: p.y });
        if (p.trail.length > 5) p.trail.shift();

        p.vy += 40 * dt;
        p.vx *= 0.985;
        p.vy *= 0.985;
        p.x += p.vx * dt;
        p.y += p.vy * dt;

        const pAlpha = fadeOut * Math.max(0, 1 - elapsed * 1.2);
        if (pAlpha <= 0) return;

        // Trail
        for (let i = 0; i < p.trail.length; i++) {
          ctx.beginPath();
          ctx.arc(p.trail[i].x, p.trail[i].y, p.size * 0.3, 0, Math.PI * 2);
          ctx.fillStyle = p.color;
          ctx.globalAlpha = (i / p.trail.length) * 0.2 * pAlpha;
          ctx.fill();
        }

        // Particle
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fillStyle = p.color;
        ctx.globalAlpha = pAlpha;
        ctx.fill();

        // Glow
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size * 1.5, 0, Math.PI * 2);
        ctx.fillStyle = p.color;
        ctx.globalAlpha = pAlpha * 0.06;
        ctx.fill();
      });
    });

    ctx.globalAlpha = 1;
  };
}

// ─── Animation 2: Side Confetti Burst ───────────────────────────────────────
function createSideConfetti(w: number, h: number) {
  const cy = h / 2;

  const particles = Array.from({ length: 60 }, (_, i) => {
    const fromLeft = i < 30;
    return {
      x: fromLeft ? -5 : w + 5,
      y: cy + (Math.random() - 0.5) * h * 0.6,
      vx: (fromLeft ? 1 : -1) * (200 + Math.random() * 400),
      vy: -(20 + Math.random() * 80),
      rotation: Math.random() * Math.PI * 2,
      rotSpeed: (Math.random() - 0.5) * 15,
      width: 4 + Math.random() * 6,
      height: 2 + Math.random() * 4,
      color: randomColor(),
      gravity: 200 + Math.random() * 200,
      drag: 0.985 + Math.random() * 0.01,
    };
  });

  return (ctx: CanvasRenderingContext2D, t: number, dt: number) => {
    const fadeOut = t > 0.55 ? 1 - (t - 0.55) / 0.45 : 1;

    particles.forEach(p => {
      p.vx *= p.drag;
      p.vy += p.gravity * dt;
      p.vy *= p.drag;
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.rotation += p.rotSpeed * dt;

      ctx.save();
      ctx.globalAlpha = fadeOut * Math.min(1, t * 8);
      ctx.translate(p.x, p.y);
      ctx.rotate(p.rotation);
      ctx.fillStyle = p.color;
      ctx.fillRect(-p.width / 2, -p.height / 2, p.width, p.height);
      ctx.restore();
    });
  };
}

// ─── Animation 3: Thumbs Up Arm ────────────────────────────────────────────
function createThumbsUp(w: number, h: number) {
  const cy = h / 2;
  const offX = w + 50;
  const stopX = w * 0.62;
  const skin = '#ffcc80';
  const skinDark = '#f0a830';
  const sleeve = '#3b82f6';
  const sleeveDark = '#2563eb';

  return (ctx: CanvasRenderingContext2D, t: number, _dt: number) => {
    let handX: number;
    let thumbWiggle = 0;

    if (t < 0.18) {
      // Slide in with easeOutBack overshoot
      const p = t / 0.18;
      const c1 = 1.70158;
      const c3 = c1 + 1;
      const eased = 1 + c3 * Math.pow(p - 1, 3) + c1 * Math.pow(p - 1, 2);
      handX = lerp(offX, stopX, eased);
    } else if (t < 0.22) {
      // Settle bounce
      const p = (t - 0.18) / 0.04;
      handX = stopX - Math.sin(p * Math.PI) * 5;
    } else if (t < 0.58) {
      // Hold with thumb wiggle
      handX = stopX;
      thumbWiggle = Math.sin((t - 0.22) * 14) * 0.1;
    } else if (t < 0.78) {
      // Retract
      const p = (t - 0.58) / 0.2;
      handX = lerp(stopX, offX, p * p * p);
    } else {
      return; // off screen
    }

    ctx.save();
    ctx.translate(handX, cy);

    // Arm extending to the right (off-screen)
    ctx.fillStyle = sleeve;
    ctx.beginPath();
    ctx.roundRect(0, -13, 60, 26, 0);
    ctx.fill();

    // Sleeve cuff
    ctx.fillStyle = sleeveDark;
    ctx.beginPath();
    ctx.roundRect(-4, -15, 16, 30, 3);
    ctx.fill();

    // Wrist
    ctx.fillStyle = skin;
    ctx.beginPath();
    ctx.roundRect(-18, -10, 18, 20, 5);
    ctx.fill();

    // Fist
    ctx.beginPath();
    ctx.arc(-22, 0, 11, 0, Math.PI * 2);
    ctx.fillStyle = skin;
    ctx.fill();

    // Finger bumps on front of fist
    ctx.beginPath();
    ctx.arc(-30, -4, 3.5, 0, Math.PI * 2);
    ctx.arc(-31, 1, 3, 0, Math.PI * 2);
    ctx.arc(-30, 6, 3, 0, Math.PI * 2);
    ctx.fill();

    // Thumb pointing up (with wiggle rotation)
    ctx.save();
    ctx.translate(-20, -8);
    ctx.rotate(thumbWiggle - 0.15);
    ctx.fillStyle = skin;
    ctx.beginPath();
    ctx.roundRect(-4, -22, 9, 22, 4.5);
    ctx.fill();

    // Thumbnail
    ctx.fillStyle = skinDark;
    ctx.beginPath();
    ctx.roundRect(-2, -21, 5, 5, [2.5, 2.5, 0, 0]);
    ctx.fill();
    ctx.restore();

    // Finger creases on fist
    ctx.strokeStyle = skinDark;
    ctx.lineWidth = 0.6;
    ctx.globalAlpha = 0.35;
    ctx.beginPath();
    ctx.moveTo(-30, -2);
    ctx.lineTo(-14, -2);
    ctx.moveTo(-30, 2);
    ctx.lineTo(-14, 2);
    ctx.moveTo(-29, 6);
    ctx.lineTo(-15, 6);
    ctx.stroke();
    ctx.globalAlpha = 1;

    ctx.restore();
  };
}

// ─── Animation 4: High Five Celebration ─────────────────────────────────────
function createHighFive(w: number, h: number) {
  const cx = w / 2;
  const cy = h / 2;
  const groundY = cy + 8;

  const startA = -40;
  const startB = w + 40;
  const meetA = cx - 18;
  const meetB = cx + 18;

  // Impact sparkles at the high-five point
  const sparks = Array.from({ length: 12 }, () => ({
    angle: Math.random() * Math.PI * 2,
    speed: 15 + Math.random() * 35,
    size: 1 + Math.random() * 2,
    color: Math.random() > 0.5 ? COLORS.gold : COLORS.white,
  }));

  function drawPerson(
    ctx: CanvasRenderingContext2D,
    x: number, baseY: number,
    bodyColor: string, headColor: string,
    faceDir: number, armRaise: number,
    legPhase: number, jumpY: number,
  ) {
    const y = baseY + jumpY;
    const headR = 7;
    const bodyW = 13;
    const bodyH = 13;
    const legH = 9;

    // Legs
    const legSwing = Math.sin(legPhase) * 4;
    ctx.fillStyle = '#555';
    ctx.beginPath();
    ctx.roundRect(x - 4 + legSwing, y + bodyH / 2, 3.5, legH, 2);
    ctx.roundRect(x + 0.5 - legSwing, y + bodyH / 2, 3.5, legH, 2);
    ctx.fill();

    // Body
    ctx.fillStyle = bodyColor;
    ctx.beginPath();
    ctx.roundRect(x - bodyW / 2, y - bodyH / 2, bodyW, bodyH, 4);
    ctx.fill();

    // Arms
    const shoulderY = y - bodyH / 2 + 3;
    ctx.strokeStyle = headColor;
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';

    // Near arm (toward center, for high five)
    const nearHandX = x + faceDir * lerp(6, 11, armRaise);
    const nearHandY = lerp(shoulderY + 7, shoulderY - 13, armRaise);
    ctx.beginPath();
    ctx.moveTo(x + faceDir * (bodyW / 2 - 1), shoulderY);
    ctx.lineTo(nearHandX, nearHandY);
    ctx.stroke();

    // Hand dot
    ctx.fillStyle = headColor;
    ctx.beginPath();
    ctx.arc(nearHandX, nearHandY, 2.5, 0, Math.PI * 2);
    ctx.fill();

    // Far arm (at side, slight swing)
    ctx.strokeStyle = headColor;
    ctx.beginPath();
    ctx.moveTo(x - faceDir * (bodyW / 2 - 1), shoulderY);
    ctx.lineTo(x - faceDir * 6, shoulderY + 6);
    ctx.stroke();

    // Head
    const headY = y - bodyH / 2 - 2 - headR;
    ctx.fillStyle = headColor;
    ctx.beginPath();
    ctx.arc(x, headY, headR, 0, Math.PI * 2);
    ctx.fill();

    // Eyes
    ctx.fillStyle = '#333';
    ctx.beginPath();
    ctx.arc(x + faceDir * 1 - 2, headY - 1, 0.9, 0, Math.PI * 2);
    ctx.arc(x + faceDir * 1 + 2, headY - 1, 0.9, 0, Math.PI * 2);
    ctx.fill();

    // Smile (bigger when celebrating)
    const smileSize = armRaise > 0.5 ? 3 : 2;
    ctx.beginPath();
    ctx.arc(x + faceDir * 0.5, headY + 2, smileSize, 0.15, Math.PI - 0.15);
    ctx.strokeStyle = '#333';
    ctx.lineWidth = 0.7;
    ctx.stroke();
  }

  return (ctx: CanvasRenderingContext2D, t: number, _dt: number) => {
    let xA: number, xB: number;
    let armRaise = 0;
    let legPhase = 0;
    let jumpY = 0;
    let alpha = 1;

    if (t < 0.25) {
      // Walk in from both sides
      const p = t / 0.25;
      const eased = 1 - Math.pow(1 - p, 3);
      xA = lerp(startA, meetA, eased);
      xB = lerp(startB, meetB, eased);
      legPhase = t * 30;
    } else if (t < 0.35) {
      // Raise arms for high five
      const p = (t - 0.25) / 0.1;
      xA = meetA;
      xB = meetB;
      armRaise = 1 - Math.pow(1 - p, 3);
    } else if (t < 0.7) {
      // Jump up and down celebrating
      xA = meetA;
      xB = meetB;
      armRaise = 1;
      const jt = (t - 0.35) / 0.35;
      const bounce = jt * 3;
      const amp = 12 * Math.max(0, 1 - jt * 0.7);
      jumpY = -Math.abs(Math.sin(bounce * Math.PI)) * amp;
    } else {
      // Fade out
      xA = meetA;
      xB = meetB;
      armRaise = 1;
      alpha = 1 - (t - 0.7) / 0.3;
    }

    ctx.save();
    ctx.globalAlpha = alpha;

    // Draw both people
    drawPerson(ctx, xA, groundY, COLORS.red, '#ffcc80', 1, armRaise, legPhase, jumpY);
    drawPerson(ctx, xB, groundY, COLORS.blue, '#e8b06a', -1, armRaise, legPhase, jumpY);

    // High-five impact sparkles
    if (t >= 0.33 && t < 0.55) {
      const st = (t - 0.33) / 0.22;
      const sparkAlpha = alpha * (1 - st);
      const sparkY = groundY - 13 + jumpY;

      sparks.forEach(s => {
        const sx = cx + Math.cos(s.angle) * s.speed * st;
        const sy = sparkY + Math.sin(s.angle) * s.speed * st;
        ctx.beginPath();
        ctx.arc(sx, sy, s.size * (1 - st * 0.5), 0, Math.PI * 2);
        ctx.fillStyle = s.color;
        ctx.globalAlpha = sparkAlpha;
        ctx.fill();
      });
    }

    ctx.restore();
  };
}

// ─── Animation 5: Airplane with Banner ──────────────────────────────────────
function createAirplaneBanner(w: number, h: number) {
  const cy = h / 2;
  const startX = -120;
  const endX = w + 80;
  const flyY = cy - 5;

  return (ctx: CanvasRenderingContext2D, t: number, _dt: number) => {
    const planeX = lerp(startX, endX, t);
    const bob = Math.sin(t * 10) * 2;
    const planeY = flyY + bob;

    const fadeIn = Math.min(1, t * 5);
    const fadeOut = t > 0.85 ? 1 - (t - 0.85) / 0.15 : 1;

    ctx.save();
    ctx.globalAlpha = fadeIn * fadeOut;

    // ── Banner (behind plane) ──
    const bannerW = 110;
    const bannerH = 20;
    const ropeLen = 20;
    const bannerX = planeX - 18 - ropeLen - bannerW;
    const bannerY = planeY + 3;

    // Rope from tail to banner
    ctx.strokeStyle = '#aaa';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(planeX - 16, planeY + 1);
    ctx.lineTo(bannerX + bannerW, bannerY);
    ctx.stroke();

    // Banner with slight wave
    const wave1 = Math.sin(t * 8) * 2;
    const wave2 = Math.sin(t * 8 + 1.5) * 1.5;

    ctx.fillStyle = '#ffffff';
    ctx.strokeStyle = COLORS.red;
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(bannerX, bannerY - bannerH / 2 + wave1);
    ctx.lineTo(bannerX + bannerW, bannerY - bannerH / 2 + wave2);
    ctx.lineTo(bannerX + bannerW, bannerY + bannerH / 2 + wave2);
    ctx.lineTo(bannerX, bannerY + bannerH / 2 + wave1);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();

    // Banner text
    ctx.fillStyle = COLORS.red;
    ctx.font = 'bold 12px Mulish, system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('Completed!', bannerX + bannerW / 2, bannerY + (wave1 + wave2) / 4);

    // ── Airplane ──
    ctx.save();
    ctx.translate(planeX, planeY);

    // Fuselage
    ctx.fillStyle = '#f0f0f0';
    ctx.beginPath();
    ctx.moveTo(18, 0);
    ctx.lineTo(12, -5);
    ctx.lineTo(-14, -5);
    ctx.lineTo(-17, -3);
    ctx.lineTo(-17, 3);
    ctx.lineTo(-14, 5);
    ctx.lineTo(12, 5);
    ctx.closePath();
    ctx.fill();
    ctx.strokeStyle = '#ccc';
    ctx.lineWidth = 0.5;
    ctx.stroke();

    // Top wing
    ctx.fillStyle = '#e0e0e0';
    ctx.beginPath();
    ctx.moveTo(-2, -5);
    ctx.lineTo(5, -5);
    ctx.lineTo(7, -15);
    ctx.lineTo(-4, -12);
    ctx.closePath();
    ctx.fill();

    // Bottom wing
    ctx.beginPath();
    ctx.moveTo(-1, 5);
    ctx.lineTo(4, 5);
    ctx.lineTo(5, 10);
    ctx.lineTo(-3, 9);
    ctx.closePath();
    ctx.fill();

    // Tail fin
    ctx.fillStyle = COLORS.red;
    ctx.beginPath();
    ctx.moveTo(-14, -5);
    ctx.lineTo(-11, -5);
    ctx.lineTo(-9, -13);
    ctx.lineTo(-16, -11);
    ctx.closePath();
    ctx.fill();

    // Horizontal stabilizer
    ctx.fillStyle = '#ddd';
    ctx.beginPath();
    ctx.moveTo(-15, -2);
    ctx.lineTo(-11, -2);
    ctx.lineTo(-9, -7);
    ctx.lineTo(-17, -6);
    ctx.closePath();
    ctx.fill();

    // Cockpit window
    ctx.fillStyle = '#7dd3fc';
    ctx.beginPath();
    ctx.arc(8, -1, 2.5, 0, Math.PI * 2);
    ctx.fill();

    // Spinning propeller
    ctx.save();
    ctx.translate(18, 0);
    ctx.rotate(t * 80);
    ctx.fillStyle = '#888';
    ctx.fillRect(-1.5, -7, 3, 14);
    ctx.restore();

    // Nose cone
    ctx.fillStyle = COLORS.red;
    ctx.beginPath();
    ctx.arc(18, 0, 3, 0, Math.PI * 2);
    ctx.fill();

    ctx.restore(); // plane translate
    ctx.restore(); // global save
  };
}

// ─── Main animations array ──────────────────────────────────────────────────
const animationFactories = [createFireworks, createSideConfetti, createThumbsUp, createHighFive, createAirplaneBanner];

const ANIMATION_DURATION = 2500; // 2.5 seconds

export function CompletionCelebration({ show, onComplete }: CompletionCelebrationProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const frameRef = useRef<number>(0);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  const startAnimation = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const parent = canvas.parentElement;
    if (!parent) return;

    // Size canvas to overflow area (larger than parent for effects that extend beyond)
    const rect = parent.getBoundingClientRect();
    const padding = 80;
    canvas.width = rect.width + padding * 2;
    canvas.height = rect.height + padding * 2;
    canvas.style.width = `${canvas.width}px`;
    canvas.style.height = `${canvas.height}px`;
    canvas.style.left = `${-padding}px`;
    canvas.style.top = `${-padding}px`;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Pick a random animation
    const factory = animationFactories[Math.floor(Math.random() * animationFactories.length)];
    const animate = factory(canvas.width, canvas.height);

    const startTime = performance.now();
    let lastTime = startTime;

    function tick(now: number) {
      const elapsed = now - startTime;
      const t = Math.min(1, elapsed / ANIMATION_DURATION);
      const dt = (now - lastTime) / 1000;
      lastTime = now;

      ctx!.clearRect(0, 0, canvas!.width, canvas!.height);
      animate(ctx!, t, dt);

      if (t < 1) {
        frameRef.current = requestAnimationFrame(tick);
      } else {
        onCompleteRef.current?.();
      }
    }

    frameRef.current = requestAnimationFrame(tick);
  }, []);

  useEffect(() => {
    if (show) {
      startAnimation();
    }
    return () => {
      if (frameRef.current) {
        cancelAnimationFrame(frameRef.current);
      }
    };
  }, [show, startAnimation]);

  if (!show) return null;

  return (
    <canvas
      ref={canvasRef}
      className="absolute pointer-events-none"
      style={{ zIndex: 10 }}
    />
  );
}
